import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Trophy, X as XIcon, Clock, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Modal that fetches a prediction_match game (with hydrated matches) and
 * displays a participant's predictions side-by-side with the real result
 * — when the admin has set it. Useful for merchants/admins to inspect
 * what each participant predicted.
 */
export default function PredictionsDetailDialog({
  open,
  onOpenChange,
  session,
}) {
  const { t } = useTranslation();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !session?.game_id) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/games/${session.game_id}`);
        if (!cancel) setGame(data);
      } catch (e) {
        if (!cancel) setError(e?.message || "Erreur");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, session?.game_id]);

  const sport = (game?.sport || "football").toLowerCase();
  const matches = game?.matches || [];
  const matchById = Object.fromEntries(matches.map((m) => [m.id, m]));
  const predictions = session?.predictions || [];
  const p = session?.participant || {};

  function statusFor(match, pred) {
    if (sport === "tennis") {
      if (!match?.real_winner) return null;
      return match.real_winner === pred?.winner ? "correct" : "wrong";
    }
    if (match?.real_score_a == null || match?.real_score_b == null) return null;
    return Number(match.real_score_a) === Number(pred?.score_a) &&
      Number(match.real_score_b) === Number(pred?.score_b)
      ? "correct"
      : "wrong";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md sm:max-w-lg"
        data-testid="prediction-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            {t("dashboard.predictions_modal_title", "Pronostics du participant")}
          </DialogTitle>
          <DialogDescription>
            <span data-testid="prediction-detail-participant" className="font-medium">
              {p.first_name} {p.last_name}
            </span>
            {p.email && !p._masked ? (
              <span className="block text-xs text-muted-foreground mt-0.5">
                {p.email}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div
            className="flex items-center justify-center py-10 text-muted-foreground"
            data-testid="prediction-detail-loading"
          >
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t("dashboard.predictions_modal_loading", "Chargement…")}
          </div>
        )}

        {error && (
          <div
            className="text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
            data-testid="prediction-detail-error"
          >
            {error}
          </div>
        )}

        {!loading && !error && game && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {predictions.length === 0 ? (
              <div
                className="text-sm text-muted-foreground italic"
                data-testid="prediction-detail-empty"
              >
                {t(
                  "dashboard.predictions_modal_empty",
                  "Aucun pronostic enregistré pour ce participant."
                )}
              </div>
            ) : (
              predictions.map((pred, idx) => {
                const m = matchById[pred.match_id];
                const teamAName = m?.team_a?.name || "Équipe A";
                const teamBName = m?.team_b?.name || "Équipe B";
                const status = statusFor(m, pred);
                const hasResult =
                  sport === "tennis"
                    ? !!m?.real_winner
                    : m?.real_score_a != null && m?.real_score_b != null;
                return (
                  <div
                    key={pred.match_id || idx}
                    className="rounded-xl border border-border bg-card/50 p-4"
                    data-testid={`prediction-detail-match-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                        {t("dashboard.predictions_match", "Match")} #{idx + 1}
                      </div>
                      {status === "correct" && (
                        <Badge
                          className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0 rounded-full"
                          data-testid={`prediction-detail-status-${idx}`}
                        >
                          ✓ {t("dashboard.predictions_correct", "Correct")}
                        </Badge>
                      )}
                      {status === "wrong" && (
                        <Badge
                          className="bg-red-500/15 text-red-700 dark:text-red-400 border-0 rounded-full"
                          data-testid={`prediction-detail-status-${idx}`}
                        >
                          <XIcon className="h-3 w-3 mr-1" />
                          {t("dashboard.predictions_wrong", "Incorrect")}
                        </Badge>
                      )}
                      {!hasResult && (
                        <Badge
                          variant="outline"
                          className="rounded-full text-[10.5px] font-medium border-amber-500/40 text-amber-700 dark:text-amber-400 inline-flex items-center gap-1"
                          data-testid={`prediction-detail-status-${idx}`}
                        >
                          <Clock className="h-3 w-3" />
                          {t("dashboard.predictions_pending", "En attente")}
                        </Badge>
                      )}
                    </div>

                    {sport === "tennis" ? (
                      <div className="mt-3 space-y-2">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          {t("dashboard.predictions_pick", "Pronostic")}
                        </div>
                        <div className="font-semibold">
                          {pred.winner === "a" ? teamAName : teamBName}
                        </div>
                        {hasResult && (
                          <div className="text-xs text-muted-foreground">
                            {t("dashboard.predictions_real", "Réel")} :{" "}
                            <span className="font-medium text-foreground">
                              {m.real_winner === "a" ? teamAName : teamBName}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-3 grid grid-cols-3 gap-3 items-center">
                        <div className="text-right text-sm font-medium truncate">
                          {teamAName}
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold tabular-nums">
                            {pred.score_a ?? "—"}
                            <span className="text-muted-foreground mx-1">:</span>
                            {pred.score_b ?? "—"}
                          </div>
                          {hasResult && (
                            <div className="text-[11px] text-muted-foreground mt-1">
                              {t("dashboard.predictions_real", "Réel")}{" "}
                              <span className="font-semibold text-foreground tabular-nums">
                                {m.real_score_a}
                                <span className="mx-0.5">:</span>
                                {m.real_score_b}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-left text-sm font-medium truncate">
                          {teamBName}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
